/**
 * O personagem do campus, aplicado ao escritório v1.
 *
 * Mesmo truque da vegetação (`officeFlora.ts`), agora nas pessoas: o
 * `ensureCharTextures` do `officeArt` começa cada frame com
 * `if (scene.textures.exists(tex)) continue;`, por isso quem registar a chave
 * primeiro fica com ela. Este módulo corre imediatamente antes e enche as 16
 * chaves de um visual com recortes do `personagem-v1.png`; o boneco pintado à
 * mão passa a não-se-desenhar, sozinho, sem um `if` novo em `officeArt.ts`.
 *
 * O que NÃO muda, e é o ponto todo:
 *   - `officeAvatars.ts` — a marcha, o `setSeated`, a sombra, a profundidade e
 *     a etiqueta continuam a mexer nas mesmas chaves com os mesmos tamanhos;
 *   - `officeArt.ts` — nem uma linha; o boneco desenhado fica de reserva e
 *     volta inteiro se o PNG faltar (ver `falhadas`);
 *   - as cadeiras — o encosto continua a tapar as costas, porque a linha das
 *     ancas do recorte cai exactamente onde `SIT_HIP` a punha.
 *
 * A folha do pacote não traz pose sentada — não existe em lado nenhum, não é
 * uma que se tenha deixado para trás. É derivada aqui do frame parado, e é a
 * única liberdade que este módulo toma: tronco intacto, pernas encurtadas pela
 * perspectiva, que é literalmente o que o `drawSit` do v1 faz em código.
 */

import type { Facing } from "@greenvoice/shared";
import type PhaserT from "phaser";

import { frameKey, marcarSentadoDeArte, sitKey } from "./officeArt";

/** A mesma folha que o campus usa — um ficheiro, dois mundos. */
const FICHEIRO = "/campus/personagem-v1.png";

/**
 * Chave de CARREGAMENTO, distinta das chaves de destino: o Phaser fica com a
 * folha 256×256 em bruto aqui, e só os recortes tomam os nomes que o mundo usa.
 */
const FONTE = "char:src:campus";

/**
 * Arte SENTADA dedicada — frente e os dois perfis, desenhados à mão.
 *
 * A pose derivada (laço de baixo) dobra o sprite de pé: serve, mas de perfil a
 * perna espremida nos 8px abaixo da anca não se lê. Estas três folhas trazem a
 * pose a sério — coxa, joelho, canela e bota separados.
 *
 * Encaixá-las custou duas tentativas falhadas, ambas pela mesma razão: partir do
 * full-res do artista e inventar escalas por zona, o que engordava a figura
 * (perfil a 26px contra 18px do boneco de pé) e esmagava a perna. O pacote traz
 * um pré-corte `-32x64-` com a pose JÁ enquadrada; encolhido UNIFORMEMENTE a
 * 0.70 dá 18px de perfil — exactamente a largura do de pé — e 42px de altura
 * contra os 40px da derivada. Nada de anamórfico: a proporção do desenho é do
 * artista, aqui só se muda a escala.
 *
 * Ancoragem pela SOLA em `PES`, como todos os frames. De perfil isso põe a coxa
 * a assentar na linha `SIT_HIP` da almofada com a canela a descer à frente dela,
 * que é como um corpo se senta — ancorar pela bainha da camisa em `SIT_HIP`
 * empurrava a bota para fora do frame. Horizontalmente centra-se o TRONCO e não
 * a caixa: de perfil as pernas esticadas puxam o centro da caixa para a frente e
 * o rabo acabava ao lado do assento.
 *
 * Sem folha para `up` (o pacote não a traz) e em qualquer 404, `sitFalhadas`
 * deixa a direcção cair na derivada — as costas voltadas escondem a perna, por
 * isso a derivada chega lá. Baker: `bake_sit.mjs`.
 */
const SENTADO: Partial<Record<Facing, { fonte: string; ficheiro: string }>> = {
  down: { fonte: "char:src:sit:down", ficheiro: "/campus/char-sit-down-v1.png" },
  left: { fonte: "char:src:sit:left", ficheiro: "/campus/char-sit-left-v1.png" },
  right: { fonte: "char:src:sit:right", ficheiro: "/campus/char-sit-right-v1.png" },
};

// ─── A folha do pacote ───────────────────────────────────────────────────────

const FRAME = 64;
const FILA: Record<Facing, number> = { down: 0, left: 1, right: 2, up: 3 };

/**
 * Passo do v1 → coluna da folha.
 *
 * O v1 anda em três frames (0 esquerdo · 1 parado · 2 direito) e a folha traz
 * quatro por direcção, onde 0 é o parado e 1 e 3 são as duas passadas opostas.
 * A coluna 2 é a passagem intermédia, que o ciclo do v1 (`[0, 1, 2, 1]`) já
 * cobre ao voltar ao parado entre passadas — usá-la dava dois meios-passos
 * seguidos e uma marcha a arrastar.
 */
const PASSO: readonly number[] = [1, 0, 3];

/**
 * A janela do corpo dentro do frame, MEDIDA na folha (as caixas de alfa dos 16
 * frames vão de x=19..45 e y=7..60, com os pés sempre na mesma linha).
 *
 * Fixa e não por-frame de propósito: recortada pela caixa de cada frame, a
 * figura saltava um pixel a cada passada — a cabeça sobe e desce sozinha
 * durante a marcha, e o olho vê isso como um tremor.
 */
const CORPO_X = 16;
const CORPO_W = 32;
const CORPO_TOPO = 7;
const CORPO_FUNDO = 60;

/**
 * A linha das ancas na folha: onde o tronco acaba e as pernas começam.
 *
 * Medida no perfil de larguras do frame parado — a figura tem 26 px de largura
 * até y=44 (tronco e braços) e estreita para 15 a partir de y=45, que são as
 * duas pernas juntas. É o único número deste módulo que veio de olhar para a
 * arte em vez de sair de uma conta.
 */
const HIP = 45;

// ─── A grelha do escritório v1 (contrato com `officeArt.ts`) ─────────────────

/**
 * Estes quatro números são um CONTRATO, escritos em literais em vez de
 * importados: `SIT_HIP` nem sequer é exportado, e os outros saem de misturar
 * `S` com coordenadas espalhadas pelos desenhos. Se alguém mudar a grelha do
 * boneco do v1, isto tem de ser uma decisão consciente — e o teste ao lado
 * torna-a barulhenta.
 *
 *   LARG × ALT  o canvas de cada frame (16*S × 24*S), origem no fundo = pés
 *   TOPO..PES   onde o boneco desenhado vive: cabelo em 2, sola do sapato em 47
 *   SIT_HIP     a linha das ancas sentado, sobre a qual `seatDropFor` alinha a
 *               almofada de cada cadeira
 */
const LARG = 32;
const ALT = 48;
const TOPO = 2;
const PES = 47;
const SIT_HIP = 39;

/** Altura útil do boneco no canvas do v1, e a escala que lá mete o do pacote. */
const ALTURA = PES - TOPO + 1;
const ESCALA = ALTURA / (CORPO_FUNDO - CORPO_TOPO + 1);
const LARGURA = Math.round(CORPO_W * ESCALA);
const DX = Math.floor((LARG - LARGURA) / 2);

/**
 * Sentado, o tronco fica intacto e as pernas encolhem.
 *
 * `TRONCO` sai da escala normal (as ancas caem em `SIT_HIP` sem forçar nada —
 * a proporção do pacote e a do v1 batem certo ao pixel, o que é a razão pela
 * qual esta pose derivada é honesta e não um esticão). As pernas é que são
 * comprimidas: de frente e de lado ficam com 9 px, o vão entre as ancas e a
 * sola no `drawSit`; de costas ficam com 5, porque o `drawSitUp` do v1 não
 * desenha pés — quem está de costas para nós tem as pernas do outro lado da
 * cadeira, e mostrá-las era pô-las a atravessar o assento.
 */
const TRONCO = Math.round((HIP - CORPO_TOPO) * ESCALA);
const PERNAS = PES - SIT_HIP;
const PERNAS_UP = 5;

const DIRS: readonly Facing[] = ["down", "left", "right", "up"];

/**
 * Cenas cujo PNG falhou a carregar.
 *
 * Existe pela armadilha de sempre: um 404 no `load.image` NÃO dá erro — o
 * Phaser mete a textura `__MISSING` (quadrado preto com contorno verde) e o
 * escritório enche-se de quadrados a andar. Aqui, uma cena marcada é saltada e
 * o boneco desenhado do v1 volta ao serviço, igual ao que era.
 *
 * `WeakMap` e não um `Set` global porque cada montagem da página cria uma cena
 * nova; assim o registo morre com ela em vez de se acumular.
 */
const falhadas = new WeakMap<PhaserT.Scene, boolean>();

/**
 * Fontes sentadas que falharam o carregamento, por cena, por chave.
 *
 * A mesma armadilha do `falhadas`, mas por direcção: um 404 numa das folhas
 * sentadas mete `__MISSING` e `exists` mente. Uma fonte aqui marcada salta a
 * arte dedicada e deixa a pose derivada do `personagem-v1` tomar a chave — cada
 * lado cai de pé por si, sem arrastar os outros.
 */
const sitFalhadas = new WeakMap<PhaserT.Scene, Set<string>>();

/**
 * Põe a folha na fila de carregamento e arma a rede de segurança.
 *
 * Tem de ser chamado no `preload` da cena: o `create` só corre depois de isto
 * acabar, e é essa garantia que permite ao `ensureOfficeChar` assumir que a
 * folha ou está pronta ou está marcada como falhada.
 */
export function preloadOfficeChar(scene: PhaserT.Scene): void {
  falhadas.set(scene, false);
  const semSentado = new Set<string>();
  sitFalhadas.set(scene, semSentado);
  scene.load.on("loaderror", (file: { key: string }) => {
    if (file.key === FONTE) falhadas.set(scene, true);
    else if (file.key.startsWith("char:src:sit:")) semSentado.add(file.key);
  });
  scene.load.image(FONTE, FICHEIRO);
  for (const s of Object.values(SENTADO)) scene.load.image(s.fonte, s.ficheiro);
}

function tela(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const canvas = document.createElement("canvas");
  canvas.width = LARG;
  canvas.height = ALT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // A redução é de 54 px para 46 — 0,85. Sem suavização a interpolação come
  // linhas inteiras a intervalos irregulares e a cara fica com um olho maior
  // que o outro; com ela, o boneco chega quase à escala nativa e limpo.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  return { canvas, ctx };
}

/** Uma faixa horizontal da folha, esticada para a faixa correspondente do canvas do v1. */
function faixa(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource,
  col: number,
  fila: number,
  sy: number,
  sh: number,
  dy: number,
  dh: number,
): void {
  ctx.drawImage(img, col * FRAME + CORPO_X, fila * FRAME + sy, CORPO_W, sh, DX, dy, LARGURA, dh);
}

/**
 * Regista os 16 frames de um visual com a arte do pacote.
 *
 * Recebe a chave e não o `CharSprite` inteiro porque as cores deixam de contar:
 * a folha traz uma pessoa só. Continua a ser chamado por avatar — `spriteFor`
 * dá uma chave por pessoa e não há forma de as saber todas de antemão — mas o
 * guarda `exists` faz da segunda chamada em diante uma passagem por 16 `if`.
 */
export function ensureOfficeChar(scene: PhaserT.Scene, key: string): void {
  if (falhadas.get(scene)) return; // sem PNG: fica o boneco desenhado do v1
  if (!scene.textures.exists(FONTE)) return; // nem chegou a carregar
  const img = scene.textures.get(FONTE).getSourceImage() as HTMLImageElement;

  for (const dir of DIRS) {
    const fila = FILA[dir];
    for (let fi = 0; fi < PASSO.length; fi += 1) {
      const tex = frameKey(key, dir, fi);
      if (scene.textures.exists(tex)) continue;
      const t = tela();
      if (!t) continue;
      faixa(t.ctx, img, PASSO[fi] ?? 0, fila, CORPO_TOPO, CORPO_FUNDO - CORPO_TOPO + 1, TOPO, ALTURA);
      scene.textures.addCanvas(tex, t.canvas);
    }
  }

  // Poses sentadas DEDICADAS: arte própria já em 32×48 (ancas em SIT_HIP, pés na
  // sola), reclamada ANTES do laço derivado abaixo — o mesmo truque de precedência
  // das 16 de andar. Cada direcção é independente: uma folha em falta (404 →
  // sitFalhadas) ou não-carregada deixa SÓ essa direcção cair na pose derivada.
  // É assim que o `up`, que não tem folha, continua derivado sem um `if` a dizê-lo.
  const semSentado = sitFalhadas.get(scene);
  for (const dir of DIRS) {
    const s = SENTADO[dir];
    if (!s) continue;
    if (semSentado?.has(s.fonte)) continue; // 404: cai na pose derivada
    if (!scene.textures.exists(s.fonte)) continue; // nem chegou a carregar
    const tex = sitKey(key, dir);
    if (scene.textures.exists(tex)) continue;
    const t = tela();
    if (!t) continue;
    t.ctx.imageSmoothingEnabled = false; // já em 32×48: cópia 1:1, sem interpolar
    t.ctx.drawImage(scene.textures.get(s.fonte).getSourceImage() as HTMLImageElement, 0, 0);
    scene.textures.addCanvas(tex, t.canvas);
    // A anca desta pose está onde a ARTE a pôs, não onde o boneco desenhado a
    // punha — e é o `seatDropFor` que precisa de saber a diferença.
    marcarSentadoDeArte(tex);
  }

  for (const dir of DIRS) {
    const tex = sitKey(key, dir);
    if (scene.textures.exists(tex)) continue;
    const t = tela();
    if (!t) continue;
    const fila = FILA[dir];
    const pernas = dir === "up" ? PERNAS_UP : PERNAS;
    // Pernas primeiro, tronco por cima: a junção fica escondida pela bainha da
    // camisa em vez de ficar uma linha a cortar a cintura ao meio.
    faixa(t.ctx, img, 0, fila, HIP, CORPO_FUNDO - HIP + 1, SIT_HIP, pernas);
    faixa(t.ctx, img, 0, fila, CORPO_TOPO, HIP - CORPO_TOPO, SIT_HIP - TRONCO, TRONCO);
    scene.textures.addCanvas(tex, t.canvas);
  }
}

/** Só para o teste: a geometria derivada, sem browser pelo meio. */
export const GEOMETRIA = {
  ALTURA,
  ESCALA,
  LARGURA,
  DX,
  TRONCO,
  PERNAS,
  PERNAS_UP,
  SIT_HIP,
  HIP,
  PASSO,
} as const;
